import React, { useState } from "react";
import { CheckCircle2, Circle, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";
import { StageCertificateDialog } from "./StageCertificateDialog";

const STATUS = { staged: ["Staged", "pill-warn"], active: ["Active", "pill-success"], retired: ["Retired", ""] };

function Step({ done, label, children, testId }) {
  const Icon = done ? CheckCircle2 : Circle;
  return (
    <li className="flex flex-wrap items-center gap-2 py-1.5 text-sm" data-testid={testId}>
      <Icon size={15} style={{ color: done ? "var(--accent)" : "var(--muted)" }} />
      <span style={{ color: done ? "var(--ink)" : "var(--muted)" }}>{label}</span>
      {children}
    </li>
  );
}

function RecordMeta({ record }) {
  return (
    <div className="text-xs mt-1 space-y-0.5" style={{ color: "var(--muted)" }}>
      <div className="font-mono break-all">SHA-1 {record.thumbprint_sha1}</div>
      <div>Valid {formatWhen(record.not_before, false)} – {formatWhen(record.expires_at, false)} · {record.days_remaining} days left</div>
      <div>Secret {record.secret_reference} · version {record.secret_version}</div>
      {record.overlap_start && <div>Overlap {formatWhen(record.overlap_start)} – {formatWhen(record.overlap_end)}</div>}
      {record.notes && <div>{record.notes}</div>}
    </div>
  );
}

function StagedChecklist({ record, busy, run, onActivate, validHours }) {
  const check = record.verification;
  const fresh = Boolean(check?.ok) && Date.now() - new Date(check.at).getTime() < validHours * 3600 * 1000;
  const small = "btn-ghost !py-1 !px-2.5 text-xs";
  return (
    <ol className="mt-3 border-l-2 pl-4" style={{ borderColor: "var(--border-strong)" }} data-testid="rotation-checklist">
      <Step done label="Public metadata staged" testId="rotation-step-staged" />
      <Step done={Boolean(record.entra_uploaded_at)} label="Public certificate uploaded in Entra (Certificates & secrets)" testId="rotation-step-uploaded">
        {!record.entra_uploaded_at && <button className={small} disabled={busy} onClick={() => run(`/admin/sso/records/${record.id}/entra-uploaded`, "Upload confirmed")} data-testid={`rotation-confirm-upload-${record.id}`}>I uploaded it</button>}
      </Step>
      <Step done={Boolean(check?.ok)} label="Server deployed the new secret version and Microsoft accepts it" testId="rotation-step-verified">
        <button className={small} disabled={busy} onClick={() => run(`/admin/sso/records/${record.id}/verify`, null)} data-testid={`rotation-verify-${record.id}`}>Verify overlap</button>
      </Step>
      {check && <li className="text-xs pl-6" style={{ color: check.ok ? "var(--accent)" : "#9B2C1F" }} data-testid={`rotation-verify-result-${record.id}`}>{formatWhen(check.at)} · {check.message}</li>}
      <Step done={false} label="Activate the new record (the previous one is retired)" testId="rotation-step-activate">
        <button className="btn-primary !py-1 !px-2.5 text-xs" disabled={busy || !record.entra_uploaded_at || !fresh} onClick={() => onActivate(record)}
          title={fresh ? "Record this certificate as active" : `Confirm the upload and pass verification within ${validHours} hours first`} data-testid={`rotation-activate-${record.id}`}>Activate</button>
      </Step>
    </ol>
  );
}

function TemplateSteps({ record, readOnly, onChanged }) {
  const [busy, setBusy] = useState(-1);
  if (!record.steps.length) return null;
  const toggle = async (index, done) => {
    setBusy(index);
    try { await api.post(`/admin/sso/records/${record.id}/steps/${index}`, { done }); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(-1); }
  };
  const done = record.steps.filter((step) => step.done).length;
  return (
    <div className="mt-3" data-testid={`rotation-steps-${record.id}`}>
      <div className="eyebrow text-[9px] mb-1">{record.template_title} · {done} of {record.steps.length} steps done</div>
      <ul className="space-y-1">
        {record.steps.map((step, index) => (
          <li key={`${record.id}-${index}`} className="text-xs">
            <label className="flex items-start gap-2 cursor-pointer" data-testid={`rotation-step-${record.id}-${index}`}>
              <input type="checkbox" className="mt-0.5" checked={step.done} disabled={readOnly || !["staged", "active"].includes(record.status) || busy === index}
                onChange={(e) => toggle(index, e.target.checked)} data-testid={`rotation-step-checkbox-${record.id}-${index}`} />
              <span style={{ color: step.done ? "var(--muted)" : "var(--ink)", textDecoration: step.done ? "line-through" : "none" }}>{step.text}</span>
              {step.done && <span style={{ color: "var(--muted)" }}>· {step.done_by}, {formatWhen(step.done_at)}</span>}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CertificateRotationCard({ records, onStepUp, onChanged, readOnly, validHours, templates }) {
  const [busy, setBusy] = useState(false);
  const [staging, setStaging] = useState(false);

  const run = async (path, success, method = "post") => {
    setBusy(true);
    try {
      const { data } = await api[method](path);
      if (success) toast.success(success);
      else toast[data.ok ? "success" : "error"](data.message);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const discard = (record) => {
    if (window.confirm("Discard this staged rotation? The active certificate is not affected.")) run(`/admin/sso/records/${record.id}`, "Staged rotation discarded", "delete");
  };
  const activate = (record) => onStepUp({
    purpose: "sso_certificate", title: "Confirm certificate activation",
    onConfirm: async (grant) => {
      await api.post(`/admin/sso/records/${record.id}/activate`, { grant });
      toast.success("New certificate recorded as active");
      onChanged();
    },
  });

  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="sso-rotation-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="eyebrow mb-1">Certificate rotation</div>
          <h3 className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>Metadata, overlap and history</h3>
        </div>
        {!readOnly && (
          <button onClick={() => setStaging(true)} className="btn-primary flex items-center gap-2 text-sm" data-testid="sso-stage-rotation">
            <KeyRound size={14} /> Stage rotation
          </button>
        )}
      </div>
      {records.map((record) => (
        <div key={record.id} className="py-4 border-t" style={{ borderColor: "var(--border)" }} data-testid={`sso-record-${record.id}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`pill text-[9px] ${STATUS[record.status][1]}`} data-testid={`sso-record-status-${record.id}`}>{STATUS[record.status][0]}</span>
            <span style={{ color: "var(--ink)" }}>{record.subject}</span>
            {record.status === "staged" && !readOnly && (
              <button className="ml-auto btn-ghost !py-1 !px-2.5 text-xs flex items-center gap-1" onClick={() => discard(record)} data-testid={`sso-record-discard-${record.id}`}><Trash2 size={12} /> Discard</button>
            )}
          </div>
          <RecordMeta record={record} />
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Staged {formatWhen(record.created_at)} by {record.created_by}
            {record.activated_at && ` · activated ${formatWhen(record.activated_at)} by ${record.activated_by}`}
            {record.retired_at && ` · retired ${formatWhen(record.retired_at)}`}
          </div>
          {record.status === "staged" && !readOnly && <StagedChecklist record={record} busy={busy} run={run} onActivate={activate} validHours={validHours} />}
          <TemplateSteps record={record} readOnly={readOnly} onChanged={onChanged} />
        </div>
      ))}
      {!records.length && <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="sso-records-empty">No certificate records yet.</p>}
      <StageCertificateDialog open={staging} onClose={() => setStaging(false)} onStaged={onChanged} templates={templates} />
    </section>
  );
}
